import { useEffect, useRef, useState } from 'react';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import settings from '@/settings/index';
import Toggle from '@/components/commons/ReebeloCareSection/Toggle';
import { CtaButton } from '@/components/Cta/Cta';
import {
  setCookieConsent,
  getCookieConsent,
  clearPosthogCookies,
  clearSegmentCookies,
  clearGoogleAnalyticsCookies,
  clearGoogleAdsCookies,
  clearMetaCookies,
  clearTikTokCookies,
  clearMicrosoftCookies,
} from '@/lib/utils/cookie-consent';
import { logger } from '@/lib/logger';
import i18n from '@/settings/i18n';

const t = i18n.init('cookies');

export default function CookieSettings() {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [advertisingEnabled, setAdvertisingEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const stored = getCookieConsent();

    setAnalyticsEnabled(
      stored?.posthog === true ||
        stored?.segment === true ||
        stored?.googleAnalytics === true,
    );
    setAdvertisingEnabled(
      stored?.googleAds === true ||
        stored?.meta === true ||
        stored?.tiktok === true ||
        stored?.microsoft === true,
    );
  }, []);

  useEffect(
    () => () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    },
    [],
  );

  function onCookieSettingsSaved() {
    logger.info('Cookie settings saved', {
      analyticsEnabled,
      advertisingEnabled,
    });
    setCookieConsent({
      posthog: analyticsEnabled,
      segment: analyticsEnabled,
      googleAnalytics: analyticsEnabled,
      googleAds: advertisingEnabled,
      meta: advertisingEnabled,
      tiktok: advertisingEnabled,
      microsoft: advertisingEnabled,
    });

    // Stop (or resume) an already-running RUM session to match the choice.
    import('@/lib/datadog-rum')
      .then(({ syncRumConsent }) => syncRumConsent())
      .catch(() => undefined);

    if (!analyticsEnabled) {
      clearPosthogCookies();
      clearSegmentCookies();
      clearGoogleAnalyticsCookies();
    }

    if (!advertisingEnabled) {
      clearGoogleAdsCookies();
      clearMetaCookies();
      clearTikTokCookies();
      clearMicrosoftCookies();
    }

    setIsSaving(true);

    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = window.setTimeout(() => {
      setIsSaving(false);
    }, 400);
  }

  return (
    <>
      <WebsiteSchema
        title={t`Cookie Settings`}
        description={t`Manage your cookie preferences for Reebelo ${{
          country: settings.country,
        }}.`}
      />
      <div className="reebelo-container max-w-[800px] space-y-6 py-8">
        <div className="text-center text-[35px] font-bold">
          {t`Cookie Settings`}
        </div>
        <p className="text-sm">
          {t`You can control how we use cookies and similar technologies. Some cookies are required for the site to function properly, while others help us improve your experience and show relevant content.`}
        </p>

        <div className="divide-y divide-gray-500 rounded-lg border border-gray-500">
          {/* Required cookies */}
          <div className="flex items-start justify-between gap-4 p-5">
            <div className="space-y-2">
              <p className="text-base font-bold">
                {t`Technically necessary cookies`}
              </p>
              <p className="text-sm">
                {t`These cookies are essential for the proper functioning of our website and cannot be disabled. They include, for example, saving items in your shopping cart, enabling secure checkout and payments, preventing fraud, and maintaining site security.`}
              </p>
            </div>

            <Toggle isActive={true} isEnabled={false} />
          </div>

          {/* Analytics cookies */}
          <div className="flex items-start justify-between gap-4 p-5">
            <div className="space-y-2">
              <p className="text-base font-bold">{t`Analytics cookies`}</p>
              <p className="text-sm">
                {t`These cookies help us understand how visitors interact with our website and improve site performance. This includes tools such as PostHog, Segment, and Google Analytics.`}
              </p>
            </div>
            <Toggle
              isActive={analyticsEnabled}
              onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
            />
          </div>

          {/* Advertising cookies */}
          <div className="flex items-start justify-between gap-4 p-5">
            <div className="space-y-2">
              <p className="text-base font-bold">{t`Advertising cookies`}</p>
              <p className="text-sm">
                {t`These cookies are used to deliver relevant ads and measure the effectiveness of advertising campaigns. This includes Google Ads, Meta, TikTok, and Microsoft Bing Ads.`}
              </p>
            </div>
            <Toggle
              isActive={advertisingEnabled}
              onClick={() => setAdvertisingEnabled(!advertisingEnabled)}
            />
          </div>
        </div>

        <CtaButton
          onClick={onCookieSettingsSaved}
          loading={isSaving}
          disabled={isSaving}
          variant="dark"
          className="
                min-h-12
                min-w-52
                flex-1
                bg-black px-4
                py-2 text-[12px] font-bold
                text-white
                transition
                sm:text-sm
                "
        >
          {t`Save`}
        </CtaButton>
      </div>
    </>
  );
}
